import { Link, useLocation } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn, resolveAssetUrl } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import { useTeams } from '@/hooks/api-hooks'
import {
  Calendar,
  Home,
  MessageSquare,
  Users,
  UserCheck,
  FileText,
  Building,
  Shield,
  Mail,
  User,
  BarChart3,
  ChevronDown,
  Check,
  Plus,
} from 'lucide-react'
import { useState } from 'react'
import type { Organization } from '@/types'

interface NavigationSection {
  section: string
  items: {
    name: string
    href: string
    icon: React.ElementType
    badge?: number
  }[]
}

const getNavigationSections = (user: any | null): NavigationSection[] => {
  const role = (user?.organizationRole ?? '').toLowerCase()
  const isOwnerOrAdmin = role === 'owner' || role === 'admin'

  const sections: NavigationSection[] = [
    {
      section: 'Overview',
      items: [{ name: 'Dashboard', href: '/dashboard', icon: Home }],
    },
    {
      section: 'My Space',
      items: [
        { name: 'My Feedback', href: '/feedback', icon: MessageSquare },
        { name: 'My 1-on-1', href: '/one-on-ones', icon: Calendar },
      ],
    },
  ]

  if (isOwnerOrAdmin) {
    sections.push(
      {
        section: 'Management',
        items: [
          { name: 'Team', href: '/team', icon: UserCheck },
          { name: 'Members', href: '/members', icon: Users },
          { name: 'Templates', href: '/templates', icon: FileText },
        ],
      },
      {
        section: 'Settings',
        items: [
          { name: 'Organization', href: '/organization', icon: Building },
          { name: 'Profile', href: '/profile', icon: User },
        ],
      },
      {
        section: 'Security',
        items: [
          { name: 'Audit Trail', href: '/audit-trail', icon: Shield },
          { name: 'Email Log', href: '/email-management', icon: Mail },
        ],
      },
    )
  }

  return sections
}

interface NavSectionProps {
  title: string
  children: React.ReactNode
}

const NavSection = ({ title, children }: NavSectionProps) => {
  return (
    <div className="py-4">
      <h3 className="px-3 mb-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

interface OrganizationSwitcherProps {
  user: any
}

const OrganizationSwitcher = ({ user }: OrganizationSwitcherProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { switchOrganization } = useAuth()

  if (!user || !Array.isArray(user.organizations) || user.organizations.length === 0) {
    return null
  }

  const currentOrg =
    user.organizations.find((org: Organization) => org.id === user.currentOrganizationId) ||
    user.organizations[0]

  const canCreateOrganization = true

  const getOrgInitials = (name?: string) => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    const initials = parts.slice(0, 2).map((part) => part.charAt(0)?.toUpperCase() ?? '').join('')
    return initials || name.charAt(0).toUpperCase() || '?'
  }

  const formatPlan = (plan?: string) => {
    if (!plan) return 'Free'
    return plan.charAt(0).toUpperCase() + plan.slice(1)
  }

  const resolveLogo = (avatar?: string | null) => resolveAssetUrl(avatar ?? null) ?? undefined

  const handleSwitchOrganization = async (orgId: string) => {
    try {
      await switchOrganization(orgId)
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to switch organization:', error)
      // Could add toast notification here
    }
  }

  return (
    <div className="px-3 py-3 border-b border-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md hover:bg-accent transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            {currentOrg.avatar ? (
              <AvatarImage src={resolveLogo(currentOrg.avatar)} alt={currentOrg.name} />
            ) : null}
            <AvatarFallback className="bg-muted text-xs text-muted-foreground">
              {getOrgInitials(currentOrg.name)}
            </AvatarFallback>
          </Avatar>
          <div className="text-left">
            <div className="font-medium">{currentOrg.name}</div>
            <div className="text-xs text-muted-foreground capitalize">
              {formatPlan(currentOrg.plan)} • {currentOrg.role}
            </div>
          </div>
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-muted-foreground transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {isOpen && (
        <div className="mt-2 space-y-1">
          {user.organizations.map((org: Organization) => (
            <button
              key={org.id}
              onClick={() => handleSwitchOrganization(org.id)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Avatar className="h-6 w-6">
                  {org.avatar ? (
                    <AvatarImage src={resolveLogo(org.avatar)} alt={org.name} />
                  ) : null}
                  <AvatarFallback className="bg-muted text-[10px] text-muted-foreground">
                    {getOrgInitials(org.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <div className="font-medium">{org.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{formatPlan(org.plan)} • {org.role}</div>
                </div>
              </div>
              {org.id === currentOrg.id && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </button>
          ))}
          {canCreateOrganization && (
            <div className="border-t border-border pt-2 mt-2">
              <Link
                to="/organization/create"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-muted rounded flex items-center justify-center">
                    <Plus className="w-3 h-3" />
                  </div>
                  <span className="text-left font-medium">Create organization</span>
                </div>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface TeamNavItemProps {
  user: any
}

const TeamNavItem = ({ user }: TeamNavItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const location = useLocation()

  // For org admins/owners: get ALL teams in organization
  // For team managers: get only teams they're members of
  const isOrgAdminOrOwner = user?.organizationRole === 'owner' || user?.organizationRole === 'admin'
  const teamsParams = isOrgAdminOrOwner
    ? { includeMembers: true }
    : { memberOfTeams: true, includeMembers: true }

  const { data: teamsData } = useTeams(teamsParams)
  const teams = (teamsData as { teams?: any[] } | undefined)?.teams ?? []

  // Check if user is a team manager (has MANAGER role in any team they're a member of)
  // Only relevant for non-org-admin users
  const isTeamManager = !isOrgAdminOrOwner && teams.some((team: any) =>
    team.members?.some((member: any) =>
      (member.user?.id ?? member.user_id) === user?.id && member.role === 'MANAGER'
    )
  )

  // Show "My Team" if user is organization admin/owner OR team manager
  const shouldShowMyTeam = user && (isOrgAdminOrOwner || isTeamManager)

  if (!shouldShowMyTeam) {
    return null
  }

  const hasTeams = teams.length > 0

  // For org admins/owners, always show "My Team" even if they have no teams yet
  // For team managers, only show if they have teams
  if (!hasTeams && !isOrgAdminOrOwner) {
    return null
  }

  const isMyTeamActive = location.pathname.startsWith('/my-team') ||
    (location.pathname.startsWith('/team/') && location.pathname !== '/team')

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors',
          isMyTeamActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
        )}
      >
        <UserCheck
          className={cn(
            'mr-3 flex-shrink-0 h-5 w-5',
            isMyTeamActive
              ? 'text-primary-foreground'
              : 'text-muted-foreground group-hover:text-foreground'
          )}
        />
        <span className="flex-grow text-left">My Team</span>
        {hasTeams && (
          <ChevronDown
            className={cn(
              'ml-auto h-4 w-4 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        )}
      </button>

      {isExpanded && (hasTeams || isOrgAdminOrOwner) && (
        <div className="ml-8 mt-1 space-y-1">
          <Link
            to="/my-team"
            className={cn(
              'block px-3 py-2 text-sm rounded-md transition-colors',
              location.pathname === '/my-team'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
          >
            Overview
          </Link>
          {hasTeams && teams.map((team: any) => (
            <Link
              key={team.id}
              to={`/team/${team.id}`}
              className={cn(
                'block px-3 py-2 text-sm rounded-md transition-colors',
                location.pathname === `/team/${team.id}`
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
            >
              {team.name}
            </Link>
          ))}
          {!hasTeams && isOrgAdminOrOwner && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              No teams yet. You can create teams from the Team management section.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function Sidebar() {
  const location = useLocation()
  const { user } = useAuth()
  const navigationSections = getNavigationSections(user)

  return (
    <div className="w-64 bg-card border-r border-border">
      <div className="flex h-16 items-center px-6">
        <Link to="/dashboard" className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">G</span>
          </div>
          <span className="font-semibold text-lg">Gathio</span>
        </Link>
      </div>

      {/* Organization Switcher */}
      <OrganizationSwitcher user={user} />

      <nav className="flex-1 px-4 pb-4">
        {navigationSections.map((section, index) => (
          <NavSection key={index} title={section.section}>
            {section.items.map((item) => {
              // Special handling for /team to avoid conflict with /team/{id}
              const isActive = item.href === '/team'
                ? location.pathname === '/team'
                : location.pathname === item.href ||
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href))

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  <item.icon
                    className={cn(
                      'mr-3 flex-shrink-0 h-5 w-5',
                      isActive
                        ? 'text-primary-foreground'
                        : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  />
                  <span className="flex-grow">{item.name}</span>
                  {item.badge !== undefined && (
                    <span className="flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
                      {item.badge}
                    </span>
                  )}
                </Link>
              )
            })}

            {/* Add My Team navigation for My Space section */}
            {section.section === 'My Space' && (
              <TeamNavItem user={user} />
            )}
          </NavSection>
        ))}
      </nav>
    </div>
  )
}
